import axios from "axios";
import { useQuery } from "react-query";

/**
 * Annotation tag × tag co-occurrence for the Heat Map Grid widget.
 *
 * WHY THIS EXISTS
 * All annotation tags live in a SINGLE array — `annotations.tags.tags.data[]` —
 * and each tag references its subTheme. When both heatmap axes are annotation-tag
 * dimensions they both resolve to that same array, so `aggrMetricsAcrossDimensions`
 * unwinds it against itself and the grid collapses onto the diagonal (the second
 * $unwind is a no-op on the already-unwound element). This is true for BOTH
 * subtheme-A × subtheme-B and subtheme-A × subtheme-A.
 *
 * The existing `/api/insightMatrix/tagCooccurrence` service already does exactly
 * the right work for this: it pair-counts tags within that single array, its
 * `sides:{ subThemeIds }` mechanism scopes tags(A) × tags(B), and it returns
 * count + strength/overlap/predictability per pair plus per-tag standalone
 * `tagCounts` (our diagonal). We call that endpoint and reshape its sparse
 * pair list into the dense grid the renderer consumes.
 *
 * LEVELS: the same collapse bites one level up — the folder-row dims
 * ("Thematics" / "Attributes" / "All Sub-Themes") are all rollups of that one
 * array, so folder × folder either degenerates to the diagonal (same role) or
 * to nothing (cross-role: one element can't be in both roles). For those the
 * call carries `level: "subTheme"`: the service pair-counts each annotation's
 * DISTINCT parent sub-themes (1 anno = 1 count per pair) and returns sub-themes
 * in the tagA/tagB slots with `subThemeId` = their own id, so side routing and
 * the reshape below work identically.
 *
 * WHAT WE OVERRIDE ON THE CALL (the "noise filters + limit" that exist by default)
 *   - view: "mostDiscussed"  — the only recipe with NO strength/overlap/minCount
 *                              floors (those live in the other recipes).
 *   - limit: very high       — the default 20 would truncate the matrix.
 *   - excludeSameSubTheme: false — default true would drop every cell in the
 *                              A×A (same-subtheme) case.
 *   - sides — explicit A/B scoping instead of the default pairScope "cross".
 *
 * Output (matches `useAggrMetricsAcrossDimensions`, so the widget's genViz is
 * unchanged):
 *   [{ value, display, count, children: [{ value, display, count }] }]
 */

const HIGH_LIMIT = 100000; // effectively unbounded — we want the full matrix

/**
 * Reshape the sparse pairs response into the dense hierarchy the HeatMapGrid
 * renderer needs. Rows = tags on the A axis, columns = tags on the B axis,
 * routed by each tag's `subThemeId`. Absent cells are explicit 0; the diagonal
 * of the symmetric (same-subtheme) case is the tag's standalone count.
 *
 * @param {{ data: Array, tagCounts: Object }} resBody  co-occurrence response body
 * @param {string[]|null} sideAIds  subThemeIds scoping the A axis, or null = all tags
 * @param {string[]|null} sideBIds  subThemeIds scoping the B axis, or null = all tags
 */
export const reshapePairsToGrid = (resBody, { sideAIds = null, sideBIds = null } = {}) => {
  const pairs = Array.isArray(resBody?.data) ? resBody.data : [];
  const tagCounts = resBody?.tagCounts || {};
  // Present only when the call asked for includeSideUniverses: every tag each
  // side admits, including ones that never co-occur or have no annotations in
  // scope. Without them, the axes are just the tags seen in a pair.
  const universeA = Array.isArray(resBody?.sideAUniverse) ? resBody.sideAUniverse : null;
  const universeB = Array.isArray(resBody?.sideBUniverse) ? resBody.sideBUniverse : null;

  // Collect display + subTheme for every tag seen in a pair or a universe.
  const tagInfo = new Map(); // id -> { display, subThemeId }
  const noteTag = (t) => {
    if (t && t._id && !tagInfo.has(t._id)) {
      tagInfo.set(t._id, { display: t.display || t._id, subThemeId: t.subThemeId || null });
    }
  };
  pairs.forEach((p) => { noteTag(p.tagA); noteTag(p.tagB); });
  [...(universeA || []), ...(universeB || [])].forEach(noteTag);

  const setA = sideAIds?.length ? new Set(sideAIds.map(String)) : null;
  const setB = sideBIds?.length ? new Set(sideBIds.map(String)) : null;
  const inA = (info) => (setA ? setA.has(info?.subThemeId) : true);
  const inB = (info) => (setB ? setB.has(info?.subThemeId) : true);
  const sameAxis = setA && setB
    ? setA.size === setB.size && [...setA].every((id) => setB.has(id))
    : !setA && !setB;

  // Cell lookup: "rowId|colId" -> count. A pair (x,y) can land on the grid as
  // (x∈A, y∈B) and/or (y∈A, x∈B); populate whichever orientation(s) qualify so
  // the symmetric case fills both triangles.
  const cell = new Map();
  pairs.forEach((p) => {
    const aId = p.tagA?._id;
    const bId = p.tagB?._id;
    if (!aId || !bId) return;
    const aInfo = tagInfo.get(aId);
    const bInfo = tagInfo.get(bId);
    if (inA(aInfo) && inB(bInfo)) cell.set(`${aId}|${bId}`, p.count);
    if (inA(bInfo) && inB(aInfo)) cell.set(`${bId}|${aId}`, p.count);
  });

  // Axis universes, most-frequent-first (deterministic) by standalone count.
  const byCountDesc = (a, b) => (tagCounts[b] || 0) - (tagCounts[a] || 0);
  const axisIds = (universe, inSide) =>
    universe
      ? [...new Set(universe.map((t) => t?._id).filter(Boolean))]
      : [...tagInfo.keys()].filter((id) => inSide(tagInfo.get(id)));
  const rowIds = axisIds(universeA, inA).sort(byCountDesc);
  const colIds = axisIds(universeB, inB).sort(byCountDesc);

  return rowIds.map((rowId) => ({
    value: rowId,
    display: tagInfo.get(rowId).display,
    count: tagCounts[rowId] || 0,
    children: colIds.map((colId) => {
      // Diagonal of the symmetric case: a tag vs itself never co-occurs (deduped
      // per annotation), so surface its standalone count instead of 0.
      const count =
        sameAxis && rowId === colId
          ? tagCounts[rowId] || 0
          : cell.get(`${rowId}|${colId}`) || 0;
      return {
        value: colId,
        display: tagInfo.get(colId).display,
        count,
        // The column's own standalone count. The grid orders columns by this
        // when present, so both axes sort by the same measure and the
        // same-axis diagonal stays on the diagonal.
        total: tagCounts[colId] || 0,
      };
    }),
  }));
};

/**
 * @param {Object|null} sideA  { subThemeIds: [id, …] } scoping the vertical axis, or null (= all tags)
 * @param {Object|null} sideB  { subThemeIds: [id, …] } scoping the horizontal axis, or null (= all tags)
 * @param {string} level  "tag" (axes = tags) or "subTheme" (axes = sub-themes — see LEVELS above)
 */
export const useTagCooccurrenceMatrix = ({
  activeFilters = [],
  // Raw preset findQuery merged into the BE $match — parity with the aggr path.
  findQuery,
  contentType = "annotations",
  sideA = null,
  sideB = null,
  level = "tag",
  // Ask for every tag each side admits, so tags with no pairs still get a
  // row/column of zeros. Off when the widget hides zero values.
  includeSideUniverses = false,
  vizCardId,
  enabled = true,
}) => {
  const sideAIds = sideA?.subThemeIds?.length ? sideA.subThemeIds : null;
  const sideBIds = sideB?.subThemeIds?.length ? sideB.subThemeIds : null;

  const body = {
    contentType,
    activeFilters,
    findQuery,
    view: "mostDiscussed",       // recipe with no strength/overlap/minCount noise
    limit: HIGH_LIMIT,           // full matrix, not top-20
    excludeSameSubTheme: false,  // keep same-subtheme pairs (A×A needs this)
    sides: { sideA, sideB },
    level,
    includeSideUniverses,
  };
  const serialized = JSON.stringify(body);

  return useQuery({
    queryKey: ["useTagCooccurrenceMatrix", vizCardId, serialized],
    queryFn: () =>
      axios
        .post("/api/insightMatrix/tagCooccurrence", body, {
          headers: { "Content-Type": "application/json" },
        })
        .then((r) => reshapePairsToGrid(r.data, { sideAIds, sideBIds })),
    enabled,
    staleTime: 60 * 1000,
  });
};
